/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOrganizationInvite } from '../../api';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type InviteDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function InviteDialog({ actionDialogReturn, onNavigate: _onNavigate }: InviteDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { activeOrganization, setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'invite') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit = !!actionDialog.organizationId;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'invite' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      if (activeOrganization) {
        const result = await createOrganizationInvite(
          session.accessToken,
          activeOrganization.id,
          {
            role: actionDialog.role,
            emailHint: actionDialog.emailHint.trim() || undefined
          }
        );
        setActionDialog({
          kind: 'invite-reveal',
          inviteUrl: result.inviteUrl || '',
          role: result.role,
          emailHint: result.emailHint
        });
        setWorkspaceVersion((v) => v + 1);
      }
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <div className="dialogPanel" onClick={(e) => e.stopPropagation()}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Members</div>
            <h3>Invite a collaborator</h3>
            <p>Generate a one-time invite link to share via Slack or email. No SMTP required.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <form className="settingsForm" onSubmit={submit}>
          <label className="settingsField">
            <span>Role</span>
            <select
              value={actionDialog.role}
              onChange={(e) => setActionDialog({ ...actionDialog, role: e.target.value as 'admin' | 'member' | 'viewer' })}
            >
              <option value="admin">Admin — full workspace access</option>
              <option value="member">Member — standard access</option>
              <option value="viewer">Viewer — read-only access</option>
            </select>
            <p className="fieldHint">Choose the permission level for this invite.</p>
          </label>
          <label className="settingsField">
            <span>Restrict to email (optional)</span>
            <input
              type="email"
              placeholder="colleague@company.com"
              value={actionDialog.emailHint}
              onChange={(e) => setActionDialog({ ...actionDialog, emailHint: e.target.value })}
            />
            <p className="fieldHint">If set, only an account with this email can accept the invite.</p>
          </label>
          {dialogError && <p className="fieldError">{dialogError}</p>}
          <div className="dialogActions">
            <button type="button" className="secondaryButton" onClick={close}>Cancel</button>
            <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
              {dialogBusy ? 'Generating…' : 'Generate invite link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { InviteDialog };
